/**
 * Presenting the four decision properties (FR-05, FR-06).
 *
 * The catalogue stores every size in centimetres (SPEC-002 D-2) so that plants
 * are comparable; a 25 m oak and a 5 cm thyme are the same field. Turning that
 * back into something a human reads is this module's job, and it is pure — no
 * `Intl` locale objects are threaded through, because a size range is digits and
 * a unit in all four of our locales.
 */

/**
 * The decision properties, in the order the mockups render them.
 *
 * One list, drawn twice (SPEC-024 D-3): compactly on the first screen, in the
 * short vocabulary the filter tiles already speak, and again below the fold with
 * the full value and its sentence. There was a `GLANCE_PROPERTIES` subset here
 * for one revision; the moment the first screen carried all four it was this
 * list under another name, and two names for one list is how a page starts
 * contradicting itself.
 */
export const DECISION_PROPERTIES = ['water', 'sun', 'soil', 'size'];

/**
 * The note under a decision's value — "Water when soil is mostly dry."
 *
 * A note describes the VALUE, not the property, so it is keyed off the value's
 * own message key. Generating the sentence from the score in code would have
 * shipped it in English to three of the four locales (SPEC-003 D-2).
 */
export function propertyNoteKey(labelKey) {
    return `${labelKey}.note`;
}

/**
 * Above 3 m a plant is described in metres, because "300–500 cm" is a number a
 * gardener has to convert in their head before it means anything. Below it,
 * centimetres — "0.05–0.1 m" for creeping thyme is worse than useless.
 */
const METRE_THRESHOLD_CM = 300;

/**
 * Is this a plant you stand under, or one you stand over?
 *
 * The browse card asks, because it swaps its size pictogram on the answer — a
 * tree for Ginkgo and Serviceberry, a sprout for Lavender. The card must not
 * re-declare the threshold: a card drawing a tree beside "60–90 cm" is exactly
 * the kind of drift that happens when two files each own half a rule.
 */
export function describesSizeInMetres({ minCm } = {}) {
    return minCm >= METRE_THRESHOLD_CM;
}

/** Centimetres back to something readable. */
export function formatSizeRange({ minCm, maxCm }) {
    if (describesSizeInMetres({ minCm })) {
        const toMetres = (value) => Number((value / 100).toFixed(1)).toString();

        return `${toMetres(minCm)}–${toMetres(maxCm)} m`;
    }

    return `${minCm}–${maxCm} cm`;
}

/**
 * The message key for a property's icon-and-name pair, so the pictogram row can
 * be driven by the property name alone.
 */
export function propertyMessageKey(property) {
    return `plants.property.${property}`;
}
